import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Target,
  TrendingUp,
  Upload,
  ArrowRight,
  Star,
  Calendar,
  CheckCircle,
  Clock,
  AlertCircle,
  Sparkles,
  BarChart3
} from 'lucide-react';
import { Button } from '@shared/components/ui/Button';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '@shared/lib/supabase';
import { cn } from '@shared/utils/cn';

interface JobMatch {
  id: string;
  job_title: string;
  company_name: string;
  match_score: number;
  status: string;
  created_at: string;
  is_favorite: boolean;
}

export const JobMatchDashboardCards: React.FC = () => {
  const { user } = useAuth();
  const [recentMatches, setRecentMatches] = useState<JobMatch[]>([]);
  const [matchCount, setMatchCount] = useState(0);
  const [avgScore, setAvgScore] = useState(0);
  const [topScore, setTopScore] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadJobMatchData();
    }
  }, [user]);

  const loadJobMatchData = async () => {
    try {
      setIsLoading(true);

      // Get recent matches (last 3)
      const { data: matches, error: matchesError } = await supabase
        .from('job_matches')
        .select('*')
        .eq('user_id', user!.id)
        .order('created_at', { ascending: false })
        .limit(3);

      if (matchesError) throw matchesError;

      setRecentMatches(matches || []);

      // Get total count and statistics
      const { data: allMatches, error: statsError } = await supabase
        .from('job_matches')
        .select('match_score')
        .eq('user_id', user!.id)
        .not('match_score', 'is', null);

      if (statsError) throw statsError;

      setMatchCount(allMatches?.length || 0);

      if (allMatches && allMatches.length > 0) {
        const scores = allMatches.map(m => m.match_score).filter(s => s !== null);
        const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
        const top = Math.max(...scores);
        setAvgScore(Math.round(avg));
        setTopScore(top);
      }
    } catch (error) {
      console.error('Error loading job match data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getScoreColor = (score: number): string => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-blue-600';
    if (score >= 40) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBgColor = (score: number): string => {
    if (score >= 80) return 'bg-green-50 border-green-200';
    if (score >= 60) return 'bg-blue-50 border-blue-200';
    if (score >= 40) return 'bg-yellow-50 border-yellow-200';
    return 'bg-red-50 border-red-200';
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { bg: string; text: string; icon: any }> = {
      completed: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle },
      analyzing: { bg: 'bg-blue-100', text: 'text-blue-800', icon: Clock },
      failed: { bg: 'bg-red-100', text: 'text-red-800', icon: AlertCircle },
      saved: { bg: 'bg-gray-100', text: 'text-gray-800', icon: Star },
    };

    const config = statusConfig[status] || statusConfig.saved;
    const Icon = config.icon;

    return (
      <span className={cn('px-2 py-1 text-xs font-medium rounded-full inline-flex items-center gap-1', config.bg, config.text)}>
        <Icon className="h-3 w-3" />
        {status}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-border/40 p-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Job Match AI Overview Banner */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-6 text-white">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
              <Target className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-2xl font-bold mb-1">Job Match AI</h2>
              <p className="opacity-90">
                Analyze job opportunities and match them against your profile
              </p>
            </div>
          </div>
          <Link to="/job-match">
            <Button className="bg-white text-blue-600 hover:bg-white/90">
              <Upload className="w-5 h-5 mr-2" />
              Upload New Job
            </Button>
          </Link>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl border border-border/40 p-6">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-muted-foreground text-sm">Total Matches</p>
              <h3 className="text-3xl font-bold mt-2">{matchCount}</h3>
            </div>
            <div className="h-10 w-10 rounded-full bg-blue-50 flex items-center justify-center">
              <Target className="h-5 w-5 text-blue-600" />
            </div>
          </div>
          <div className="mt-4">
            <Link to="/job-match?tab=history" className="text-blue-600 text-sm font-medium">
              View all matches →
            </Link>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-border/40 p-6">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-muted-foreground text-sm">Average Score</p>
              <h3 className={cn('text-3xl font-bold mt-2', getScoreColor(avgScore))}>
                {matchCount > 0 ? `${avgScore}%` : '-'}
              </h3>
            </div>
            <div className="h-10 w-10 rounded-full bg-indigo-50 flex items-center justify-center">
              <TrendingUp className="h-5 w-5 text-indigo-600" />
            </div>
          </div>
          <div className="mt-4">
            <p className="text-muted-foreground text-sm">
              {matchCount > 0 ? 'Across all job matches' : 'No matches yet'}
            </p>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-border/40 p-6">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-muted-foreground text-sm">Best Match</p>
              <h3 className={cn('text-3xl font-bold mt-2', getScoreColor(topScore))}>
                {matchCount > 0 ? `${topScore}%` : '-'}
              </h3>
            </div>
            <div className="h-10 w-10 rounded-full bg-green-50 flex items-center justify-center">
              <Star className="h-5 w-5 text-green-600" />
            </div>
          </div>
          <div className="mt-4">
            <p className="text-muted-foreground text-sm">
              {matchCount > 0 ? 'Your highest compatibility' : 'Start analyzing jobs'}
            </p>
          </div>
        </div>
      </div>

      {/* Recent Matches */}
      {recentMatches.length > 0 ? (
        <div className="bg-white rounded-xl border border-border/40 p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold">Recent Job Matches</h3>
            <Link to="/job-match?tab=history">
              <Button variant="ghost" size="sm">
                View All
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>

          <div className="space-y-4">
            {recentMatches.map((match) => (
              <div
                key={match.id}
                className="flex flex-col md:flex-row md:items-center justify-between p-4 border border-border/40 rounded-lg hover:border-primary/40 transition-colors"
              >
                <div className="flex items-start gap-4 mb-3 md:mb-0">
                  <div className={cn('w-16 h-16 rounded-lg border-2 flex items-center justify-center', getScoreBgColor(match.match_score))}>
                    <span className={cn('text-2xl font-bold', getScoreColor(match.match_score))}>
                      {match.match_score || '-'}
                    </span>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-gray-900">{match.job_title}</h4>
                      {match.is_favorite && (
                        <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">{match.company_name}</p>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {new Date(match.created_at).toLocaleDateString()}
                      </div>
                      {getStatusBadge(match.status)}
                    </div>
                  </div>
                </div>
                <Link to={`/job-match?match=${match.id}&tab=analysis`}>
                  <Button variant="outline" size="sm">
                    View Analysis
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-border/40 p-12 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
            <Target className="h-8 w-8 text-blue-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            No Job Matches Yet
          </h3>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            Start analyzing job opportunities to see how well they match your profile and receive AI-powered insights.
          </p>
          <Link to="/job-match">
            <Button>
              <Upload className="mr-2 h-4 w-4" />
              Upload Your First Job
            </Button>
          </Link>
        </div>
      )}

      {/* Call to Action - Career Intelligence Reports */}
      {recentMatches.length > 0 && (
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl p-6 text-white">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <BarChart3 className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-xl font-bold mb-1">
                  Enhance Your Applications with Career Intelligence Reports
                </h3>
                <p className="opacity-90 text-sm">
                  Get comprehensive analysis, STAR stories, and interview strategies tailored to each job
                </p>
              </div>
            </div>
            <Link to="/reports">
              <Button className="bg-white text-purple-600 hover:bg-white/90 whitespace-nowrap">
                <Sparkles className="mr-2 h-4 w-4" />
                Generate Report
              </Button>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobMatchDashboardCards;
